import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ShadowPageService } from 'src/app/insights/pages/shadow-page.service';
import {
  IDeviceShadowRecord,
  ShadowQueryRequest,
} from '../../shared/services/device-shadow/device-shadow.service';
import {
  Filter,
  FilterType,
  QueryOptions,
  RawShadowQuery,
  ValueType,
} from '../query-builder/query-builder.component';

// This is the base component for pages that display device shadow data in a table layout. To use this base class, extend this class,
// and then all functionalities should be available in your derived component and in your component template. Also, requires the derived
// component to declare the InsightsShadowPageService in its component level list of providers.
@Component({
  template: '',
  styleUrls: ['./shadow-page-base.component.scss'],
})
export class ShadowPageBaseComponent implements OnInit, OnDestroy {
  @ViewChild(AgGridAngular) grid!: AgGridAngular;

  pageCount: number;
  pageNumber = 1;
  pageSize = 40;
  query: RawShadowQuery = {};
  queryOptions: QueryOptions;
  records: IDeviceShadowRecord[];
  request: ShadowQueryRequest = {};
  totalCount = 0;
  isLoading = false;

  constructor(
    protected shadowPageService: ShadowPageService,
    protected changeDetector: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  get columnDefinitions() {
    return this.shadowPageService.columnDefinitions;
  }

  get defaultColumnConfiguration() {
    return this.shadowPageService.defaultColumnConfiguration;
  }

  ngOnInit(): void {
    const queryString = this.route.snapshot.queryParams['query'];

    if (queryString) {
      this.query = JSON.parse(queryString);
    }

    this.request = this.mapToShadowQueryRequest(this.query);

    this.getData();
  }

  ngOnDestroy(): void {
    this.resetScroll();
  }

  async onSearch(query: RawShadowQuery) {
    // We should reset pagination and scroll Id on every "new" search.
    this.resetPagination();

    this.setQueryParams(query);

    this.request = this.mapToShadowQueryRequest(query);

    await this.getData();
  }

  onPaginate() {
    this.pageNumber++;
    this.getData();
  }

  onClickFirst() {
    this.resetPagination();
    this.getData();
  }

  onRequestCsv() {
    this.shadowPageService.getCsv(this.request);
  }

  private async getData() {
    this.isLoading = true;
    try {
      const response = await this.shadowPageService.getData({
        ...this.request,
        count: this.pageSize,
      });
      this.records = response.results;
      this.request.scrollId = response.scrollId;
      this.totalCount = response.total;
      this.pageCount = Math.ceil(this.totalCount / this.pageSize);
      this.changeDetector.markForCheck();
    } catch (error) {
      console.error('Error fetching Shadow Query Data ', error);
      this.records = [];
    } finally {
      this.isLoading = false;
      this.changeDetector.markForCheck();
    }
  }

  private mapToShadowQueryRequest(query: RawShadowQuery): ShadowQueryRequest {
    return {
      companyId: query.companyId ? query.companyId : null,
      can1Type: query.can1Type ? query.can1Type : null,
      platforms:
        query.platforms && query.platforms.length > 0 ? query.platforms : null,
      ...this.mapFilters(query.filters),
      ...this.mapFilters(this.queryOptions.baseFilters),
    };
  }

  private mapFilters(filters: Filter[]) {
    return filters?.reduce((previous, filter) => {
      if (filter.valueType === ValueType.Exists) {
        if (!previous['exists']) {
          previous['exists'] = [filter.propertyName];
          return previous;
        }

        previous['exists'] = [...previous['exists'], filter.propertyName];
        return previous;
      }

       if (filter.valueType === ValueType.DoesNotExist) {
        if (!previous['doesNotExist']) {
          previous['doesNotExist'] = [filter.propertyName];
          return previous;
        }

        previous['doesNotExist'] = [
          ...previous['doesNotExist'],
          filter.propertyName,
        ];
        return previous;
      }

      const value =
        filter.valueType === ValueType.Date
          ? new Date(filter.value).toISOString()
          : filter.value;

if (filter.filterType === FilterType.Equals) {
        previous[filter.propertyName] = filter.value;
        return previous;
      }

      if (!previous[filter.propertyName]) {
        previous[filter.propertyName] = {};
      }

      previous[filter.propertyName][filter.filterType] = value;

      return previous;
    }, {});
  }

  private setQueryParams(rawQuery: RawShadowQuery) {
    let queryString: string;
    if (Object.keys(rawQuery).length > 0) {
      queryString = JSON.stringify(rawQuery);
    }

    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: queryString ?? '',
      },
    });
  }

  private resetScroll() {
    if (this.request.scrollId) {
      this.shadowPageService.deleteScrollId(this.request.scrollId);
    }

    this.request.scrollId = null;
  }

  private resetPagination() {
    this.resetScroll();
    this.pageNumber = 1;
  }
}
